"use client";

import Link from "next/link";
import { ArrowRight } from "lucide-react";
import { ThemeName, BUTTON_PRIMARY, BUTTON_SECONDARY } from "./theme";

type ButtonVariant = "primary" | "secondary" | "tertiary";
type ButtonSize = "base" | "lg" | "xl" | "sm";

const sizeMap: Record<ButtonSize, string> = {
  sm: "text-xs lg:text-sm 3xl:text-sm",
  base: "text-xs lg:text-sm 3xl:text-base",
  lg: "text-sm lg:text-base 3xl:text-lg",
  xl: "text-sm lg:text-base 3xl:text-xl",
};

interface CustomButtonProps {
  label: string;
  href?: string;
  target?: string;
  onClick?: () => void;
  variant?: ButtonVariant;
  size?: ButtonSize;
  theme?: ThemeName;
  withIcon?: boolean;
  className?: string;
  enableGap?: boolean;
  border?: boolean;
}

const CustomButton = ({
  label,
  href,
  target,
  onClick,
  variant = "primary",
  size = "base",
  theme = "blue",
  withIcon = false,
  className = "",
  enableGap = false,
  border = false,
}: CustomButtonProps) => {
  const isPrimary = variant === "primary";
  const textSize = sizeMap[size];
  const { colorClasses, arrowColor, borderClass } = BUTTON_PRIMARY[theme];
  const borderClasses = isPrimary && border ? borderClass : "";

  const primaryWithIcon = `group inline-flex items-center justify-center gap-2 rounded-lg py-[6px] px-[10px] font-onest ${textSize} font-medium shadow-[inset_0_0_8.8px_0_rgba(255,255,255,0.5)] transition-all duration-300 hover:shadow-[inset_0_0_10px_0_#FFF] lg:gap-2.5 lg:rounded-[12px] lg:py-[7px] lg:px-3 3xl:gap-[14px] 3xl:rounded-[12px] 3xl:py-[8px] 3xl:px-[16px] ${colorClasses} ${borderClasses}`;

  const primaryNoIcon = `inline-flex items-center justify-center rounded-lg py-2 px-3 font-onest ${textSize} font-medium shadow-[inset_0_0_8.8px_0_rgba(255,255,255,0.5)] transition-all duration-300 hover:shadow-[inset_0_0_10px_0_#FFF] lg:rounded-[12px] lg:py-[14px] lg:px-4 3xl:rounded-[12px] 3xl:py-[16px] 3xl:px-[18px] ${colorClasses} ${borderClasses}`;

  const secondaryBase = `group inline-flex items-center justify-center gap-2 rounded-lg border py-2 px-3 font-onest ${textSize} font-medium transition-all duration-300 lg:gap-3 lg:rounded-xl lg:py-3 lg:px-4 3xl:gap-4 3xl:rounded-[18px] 3xl:py-[16px] 3xl:px-[18px] ${BUTTON_SECONDARY[theme]}`;

  const isSecondary = variant === "secondary" || variant === "tertiary";
  const classes = isPrimary
    ? withIcon
      ? `${primaryWithIcon} ${className}`
      : `${primaryNoIcon} ${className}`
    : `${secondaryBase} ${className}`;

  const content = (
    <>
      <span
        className={`leading-none ${isSecondary && enableGap ? "duration-300 [word-spacing:10px] group-hover:[word-spacing:normal]" : ""}`}
      >
        {label}
      </span>
      {isPrimary && withIcon && (
        <span className="relative flex h-[22px] w-[22px] shrink-0 items-center justify-center overflow-hidden rounded-md bg-white lg:h-[26px] lg:w-[26px] lg:rounded-[12px] 3xl:h-[33px] 3xl:w-[33px] 3xl:rounded-[8.5px]">
          <ArrowRight
            size={16}
            style={{ color: arrowColor }}
            className="absolute transition-transform duration-300 group-hover:translate-x-[150%]"
          />
          <ArrowRight
            size={16}
            style={{ color: arrowColor }}
            className="absolute -translate-x-[150%] transition-transform duration-300 group-hover:translate-x-0"
          />
        </span>
      )}
    </>
  );

  if (href) {
    return (
      <Link
        href={href}
        onClick={onClick}
        className={classes}
        target={target}
        rel={target === "_blank" ? "noopener noreferrer" : undefined}
      >
        {content}
      </Link>
    );
  }

  return (
    <button onClick={onClick} className={classes}>
      {content}
    </button>
  );
};

export default CustomButton;
